// v18m 자연스러움 파이프라인 (Bobbi 2카드 입 리그용) — kemix-engine
// blendshape-player-natural.html 오른쪽 패널(v18m) 처리의 스트리밍 포팅.
//
// 이식 범위: Bobbi 출력(jawOpen/mouthClose 2카드 + 깜빡임)에 실제로 영향을 주는
// 스테이지만 가져왔다. 다운믹스는 jawRaw/clsRaw 만 "변환 후" 값을 읽고 나머지
// (round/spread/speech 게이트)는 전부 stage-A 프레임에서 직접 읽으므로(원본
// _applyMouthDownmix 동작), ch24/ch26 에 닿지 않는 채널 변환(rounding boost,
// funnel floor, joy-smooth, 눈썹/시선 계열)은 결과에 관여하지 않아 생략했다.
//
// 스트리밍 적응(원본은 클립 전체를 미리 알고 처리):
//  · 제로페이즈 가우시안 → 직전 청크 원본 꼬리(RAW_TAIL_FRAMES)를 좌측 컨텍스트로 연결
//  · crisp 의 클립 최대값 정규화 → 반감기 감쇠 러닝 맥스
//  · per-clip p95 노름 → StreamingNormTracker(최근 창 p95 + 워밍 스타트 prior)
//  · EMA/조이 홀드 등 인과 상태 → 청크를 넘어 유지, 발화 종료 시 리셋

import { clamp01, emaAlpha, gaussianFilter1d, smoothstep } from './filters';
import { MOUTH_SUM_CH, NormPriors, StreamingNormTracker } from './norm-tracker';

// 크리스프 대상: cheekPuff(5) + 입/턱/혀 전 채널(22~51)
const CRISP_CHANNELS: readonly number[] = [
  5, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36,
  37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51,
];
const MOUTH_CLOSE_CH = 26;
const CHANNEL_COUNT = 52;
// 립스무딩 최대 radius(σ1.75→7)를 덮는 좌측 컨텍스트 길이
const RAW_TAIL_FRAMES = 8;
// 깜빡임 벨 반폭(초) — 원본 extraBlinkAt 의 4/30
const BLINK_HALF_WIDTH = 4 / 30;

export interface NaturalPipelineParams {
  // 뷰어 리뷰 저장값(v3face-natural-2026-07-23) + 다운믹스 코드 기본값
  crispThreshold: number;
  crispScale: number;
  crispSigma: number;
  crispMouthCloseSigma: number;
  /** 스트리밍 전용: crisp 러닝 맥스 반감기(초) */
  crispMaxHalfLifeSec: number;
  lipSmooth: number;          // 가우시안 σ = lipSmooth * 5 (프레임)
  roundIsolate: number;
  shrugCap: number;
  roundOpenAmount: number;
  roundOUSplit: number;
  joyOpenAmount: number;
  jawExagTarget: number;
  articOpenAmount: number;
  mouthCloseGate: number;
  mouthDownmixOpen: number;
  blinkRate: number;          // 분당 깜빡임 횟수(BPM), 0 = 끔
}

export const DEFAULT_NATURAL_PARAMS: NaturalPipelineParams = {
  crispThreshold: 0.3,
  crispScale: 1.2,
  crispSigma: 1.0,
  crispMouthCloseSigma: 1.0,
  crispMaxHalfLifeSec: 8.0,
  lipSmooth: 0.35,
  roundIsolate: 1.0,
  shrugCap: 0.7,
  roundOpenAmount: 0.15,
  roundOUSplit: 0.1,
  joyOpenAmount: 0.5,
  jawExagTarget: 0.70,
  articOpenAmount: 0.30,
  mouthCloseGate: 0.85,
  mouthDownmixOpen: 0.25,
  blinkRate: 8,
};

export interface NaturalDownmixState {
  // 인과 상태 (발화 내 청크 간 유지, 발화 종료 시 리셋)
  joyHold: number;
  roundOpenLift: number;
  joyOpenLift: number;
  lastOutJaw: number;
  lastOutClose: number;
  rawTail: number[][];
  timelineSec: number;
  nextBlinkAt: number; // -1 = 미시드
  lastPlayAtMs: number;
  nanWarned: boolean;
  // 세션 지속 (아바타 교체 전까지 유지 — 더 나은 워밍 스타트)
  normTracker: StreamingNormTracker;
  crispRunningMax: Float32Array;
}

export function createNaturalState(priors: NormPriors): NaturalDownmixState {
  return {
    joyHold: 0,
    roundOpenLift: 0,
    joyOpenLift: 0,
    lastOutJaw: 0,
    lastOutClose: 0,
    rawTail: [],
    timelineSec: 0,
    nextBlinkAt: -1,
    lastPlayAtMs: 0,
    nanWarned: false,
    normTracker: new StreamingNormTracker(priors),
    crispRunningMax: new Float32Array(CHANNEL_COUNT),
  };
}

/** 발화 종료/인터럽트 시 인과 상태만 리셋 (노름·크리스프 맥스는 세션 유지) */
export function resetCausalState(state: NaturalDownmixState): void {
  state.joyHold = 0;
  state.roundOpenLift = 0;
  state.joyOpenLift = 0;
  state.lastOutJaw = 0;
  state.lastOutClose = 0;
  state.rawTail = [];
  state.timelineSec = 0;
  state.nextBlinkAt = -1;
}

export interface NaturalChunkOutput {
  jaw: Float32Array;          // 프레임별 JawOpen 카드 값
  close: Float32Array;        // 프레임별 mouthClose 카드 값
  blink: Float32Array | null; // 프레임별 Blink 카드 값 (비활성 시 null)
}

export function processNaturalChunk(
  rawFrames: number[][],
  fps: number,
  state: NaturalDownmixState,
  params: NaturalPipelineParams,
  opts: { blinkEnabled: boolean },
): NaturalChunkOutput {
  const frameCount = rawFrames.length;
  const jawOut = new Float32Array(frameCount);
  const closeOut = new Float32Array(frameCount);
  const blinkEnabled = opts.blinkEnabled && params.blinkRate > 0;
  const blinkOut = blinkEnabled ? new Float32Array(frameCount) : null;
  if (frameCount === 0) return { jaw: jawOut, close: closeOut, blink: blinkOut };

  const dt = 1 / fps;

  // [1] 직전 청크 꼬리 + 이번 청크를 52채널 고정 폭 버퍼로 결합 (NaN 제거)
  const tailLen = state.rawTail.length;
  const stage: Float32Array[] = new Array(tailLen + frameCount);
  const fillRow = (src: number[], dstIdx: number) => {
    const row = new Float32Array(CHANNEL_COUNT);
    const n = Math.min(CHANNEL_COUNT, src.length);
    for (let i = 0; i < n; i++) row[i] = Number.isFinite(src[i]) ? src[i] : 0;
    stage[dstIdx] = row;
  };
  for (let i = 0; i < tailLen; i++) fillRow(state.rawTail[i], i);
  for (let i = 0; i < frameCount; i++) fillRow(rawFrames[i], tailLen + i);

  // [2] Stage A1 — crisp mouth (원본 applyCrispMouth)
  const T = stage.length;
  const edge0 = params.crispThreshold * 0.3;
  const edge1 = params.crispThreshold * 1.2;
  const chunkDurSec = frameCount * dt;
  const crispDecay = Math.pow(0.5, chunkDurSec / params.crispMaxHalfLifeSec);
  const col = new Float32Array(T);
  for (const ch of CRISP_CHANNELS) {
    const sigma = ch === MOUTH_CLOSE_CH ? params.crispMouthCloseSigma : params.crispSigma;
    for (let t = 0; t < T; t++) col[t] = stage[t][ch];
    const smoothed = gaussianFilter1d(col, sigma);
    let chunkMax = 0;
    for (let t = 0; t < T; t++) if (smoothed[t] > chunkMax) chunkMax = smoothed[t];
    // 원본은 클립 전체 최대값 정규화 — 스트리밍에서는 반감기 감쇠 러닝 맥스로 대체
    state.crispRunningMax[ch] = Math.max(state.crispRunningMax[ch] * crispDecay, chunkMax);
    const maxVal = state.crispRunningMax[ch] > 0 ? state.crispRunningMax[ch] : 1;
    for (let t = 0; t < T; t++) {
      const gate = smoothstep(edge0, edge1, smoothed[t] / maxVal);
      stage[t][ch] = clamp01(smoothed[t] * gate * params.crispScale);
    }
  }

  // [3] Stage A2 — lip smooth (제로페이즈 가우시안, ch22~48 + 51)
  const lipSigma = params.lipSmooth * 5.0;
  if (lipSigma > 0) {
    for (let ch = 22; ch <= 51; ch++) {
      if (ch === 49 || ch === 50) continue; // noseSneer — 입 채널 아님
      for (let t = 0; t < T; t++) col[t] = stage[t][ch];
      const sm = gaussianFilter1d(col, lipSigma);
      for (let t = 0; t < T; t++) stage[t][ch] = sm[t];
    }
  }

  // [4] 노름 갱신 — 이번 청크 프레임만 반영 후 스냅샷 (청크 내 상수, per-clip 상수성과 대응)
  for (let i = 0; i < frameCount; i++) state.normTracker.pushFrame(stage[tailLen + i]);
  const norm = state.normTracker.snapshot();

  // [5] 프레임별 인과 루프 + 2카드 다운믹스
  const meanBlinkInterval = 60 / params.blinkRate;
  for (let i = 0; i < frameCount; i++) {
    const fb = stage[tailLen + i];

    // 프록시 (원본 applyFrame 2308-2351)
    const smile = Math.max(fb[43], fb[44]);
    const cheekSq = Math.max(fb[6], fb[7]);
    const jawOpenRaw = fb[24];
    const eyeW = Math.max(fb[20], fb[21]);
    const rSmile = clamp01((smile - 0.10) / 0.30);
    const rCheek = clamp01((cheekSq - 0.10) / 0.30);
    const rLaugh = clamp01((Math.min(smile, jawOpenRaw) - 0.10) / 0.30);
    const rExcit = clamp01((Math.min(smile, eyeW) - 0.10) / 0.30);
    const joyRamp = Math.max(rSmile, rCheek, rLaugh, rExcit);
    state.joyHold = Math.max(joyRamp, state.joyHold * Math.pow(0.5, dt / 1.0)); // 1초 반감기
    const roundSig = Math.max(fb[31], fb[37]);
    const roundRamp = clamp01((roundSig - 0.08) / 0.20 - Math.max(fb[45], fb[46]) * 2.0);

    // ch24(jawOpen) 변환 — round-open + joy-open 바닥값 (원본 2473-2511)
    let v24 = fb[24];
    if (params.roundOpenAmount > 0) {
      // ㅗ/ㅜ 개방도 분리: 모델 자체 jawOpen(p25≈0.03/p75≈0.09)으로 리프트를 스케일
      const openness = clamp01((jawOpenRaw - 0.03) / (0.09 - 0.03));
      const ouFactor = 1 - params.roundOUSplit * (1 - openness);
      const rawLift = params.roundOpenAmount * roundRamp * ouFactor;
      const tau = rawLift > state.roundOpenLift ? 0.10 : 0.03; // 느린 상승/빠른 하강
      state.roundOpenLift += emaAlpha(dt, tau) * (rawLift - state.roundOpenLift);
      if (state.roundOpenLift > v24) v24 = state.roundOpenLift;
    }
    if (params.joyOpenAmount > 0) {
      const speechRamp = smoothstep(0.02, 0.07, jawOpenRaw);
      const undershoot = smoothstep(0.04, 0.10, jawOpenRaw) * (1 - smoothstep(0.18, 0.28, jawOpenRaw));
      const rawLift = params.joyOpenAmount * state.joyHold * undershoot * speechRamp;
      state.joyOpenLift += emaAlpha(dt, 0.10) * (rawLift - state.joyOpenLift);
      if (state.joyOpenLift > v24) v24 = state.joyOpenLift;
    }
    v24 = clamp01(v24);

    // ch26(mouthClose) 변환 — roundIsolate × shrugCap (원본 2436-2458, 곱이라 순서 무관)
    let v26 = fb[26];
    v26 *= 1 - roundRamp * params.roundIsolate;
    v26 *= 1 - params.shrugCap;
    v26 = clamp01(v26);

    // 2카드 다운믹스 (원본 _applyMouthDownmix 1938-1998)
    // jawRaw/clsRaw 는 변환 후 값, r/artic/speech 는 stage-A 프레임(fb)에서 읽는 비대칭 유지
    const r = clamp01(roundSig / norm.round);
    const closeN = clamp01(v26 / norm.close);
    let tot = 0;
    for (let c = 0; c < MOUTH_SUM_CH.length; c++) tot += fb[MOUTH_SUM_CH[c]];
    const speech = smoothstep(0.25 * norm.totMouth, 0.60 * norm.totMouth, tot);
    // jawOpen 과장: 클립 p95 → jawExagTarget, 지수 곡선으로 피크만 밀어올림
    const x = clamp01(v24 / norm.jaw);
    const jawEx = params.jawExagTarget > 0 ? Math.pow(x, 1.6) * params.jawExagTarget : 0;
    // 조음 에너지 바닥값 — 다물 때(closeN)와 무발화(speech)에서는 0
    const artic = Math.max(clamp01((fb[45] + fb[46]) / 2 / norm.spread), r) * (1 - closeN) * speech;
    const lift = Math.max(jawEx, params.articOpenAmount * artic, params.mouthDownmixOpen * r);
    let jaw = clamp01(Math.max(v24, lift) * (1 - params.mouthCloseGate * closeN));
    let close = clamp01(v26 * (1 - r));
    // NaN 방어 — 모프 가중치에 NaN 이 들어가면 메시가 통째로 사라진다
    if (!Number.isFinite(jaw) || !Number.isFinite(close)) {
      if (!state.nanWarned) {
        console.warn('[natural-downmix] NaN 발생 — 직전 값으로 대체', { jaw, close, norm });
        state.nanWarned = true;
      }
      jaw = Number.isFinite(jaw) ? jaw : state.lastOutJaw;
      close = Number.isFinite(close) ? close : state.lastOutClose;
    }
    jawOut[i] = jaw;
    closeOut[i] = close;
    state.lastOutJaw = jaw;
    state.lastOutClose = close;

    // 깜빡임 스케줄 (원본 regenBlinkSchedule/extraBlinkAt — 평균 60/BPM초, ±35% 지터, 큐빅 벨)
    if (blinkOut) {
      const t = state.timelineSec + i * dt;
      if (state.nextBlinkAt < 0) {
        state.nextBlinkAt = t + meanBlinkInterval * (0.3 + Math.random() * 0.7);
      }
      while (t > state.nextBlinkAt + BLINK_HALF_WIDTH) {
        state.nextBlinkAt += meanBlinkInterval * (1 + (Math.random() - 0.5) * 0.7);
      }
      const dtb = Math.abs(t - state.nextBlinkAt);
      if (dtb < BLINK_HALF_WIDTH) {
        const bell = 1 - dtb / BLINK_HALF_WIDTH;
        blinkOut[i] = bell * bell * (3 - 2 * bell);
      }
    }
  }

  // [6] 다음 청크용 상태 이월 — 원본 마지막 ≤8프레임을 좌측 컨텍스트로 보존
  const tailSource = frameCount >= RAW_TAIL_FRAMES
    ? rawFrames.slice(-RAW_TAIL_FRAMES)
    : state.rawTail.concat(rawFrames).slice(-RAW_TAIL_FRAMES);
  state.rawTail = tailSource.map((r) => r.slice());
  state.timelineSec += frameCount * dt;

  if (typeof window !== 'undefined' && window.localStorage?.getItem('lipsync:debug')) {
    let jMin = 1, jMax = 0, jSum = 0;
    for (let i = 0; i < frameCount; i++) {
      jMin = Math.min(jMin, jawOut[i]); jMax = Math.max(jMax, jawOut[i]); jSum += jawOut[i];
    }
    console.log('[natural-downmix]', {
      frames: frameCount,
      norm,
      jaw: { min: +jMin.toFixed(3), mean: +(jSum / frameCount).toFixed(3), max: +jMax.toFixed(3) },
    });
  }

  return { jaw: jawOut, close: closeOut, blink: blinkOut };
}
